import axios, { AxiosResponse } from 'axios'
import { BaseApi } from '../couplingAjax'
import { enhanceOpt } from '../couplingAjax'
import vuex from '@/store/index'

interface QueryScanDetailData {
  id: string | number
  url: string
  file: File
  level?: number
}

interface ScanResult {
  url: string
  publicUrl: string
  status: number
  name: string
  size: number
  type: string
  level: number
  reason?: string
}

interface GetTemporaryUrlParams {
  ext: string
  contentType: string
  level?: number
}

interface GetTemporaryUrlResponse {
  object: string
  status?: number
}

interface ScanResponse {
  success: boolean
  message?: string
  object?: {
    id?: string | number
    status: number
    publicUrl: string
    reason: string
  }
}

interface CommonUploadParams {
  file: File | null
  disableDetect?: boolean
  level?: number
  onProgress?: (percentCompleted: number) => void
}

const sleep = (time: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve()
    }, time)
  })
}

const getBizApiUrl = (apiUrl: string): string => {
  const SERVIVE = vuex.state.services
  return `${SERVIVE.bizApi}${apiUrl}`
}

// 轮询健康检测结果
function queryScanDetail(data: QueryScanDetailData): Promise<ScanResult> {
  return BaseApi({
    url: getBizApiUrl(`/file/scanDetail/${data.id}`),
    method: 'get',
    error: false,
    success: false,
  } as enhanceOpt).then(async (res: any) => {
    if (res.object.status < 3) {
      await sleep(3000)
      return queryScanDetail(data)
    } else {
      return {
        url: data.url,
        publicUrl: res.object.publicUrl,
        status: res.object.status,
        name: data.file.name,
        size: data.file.size,
        type: data.file.type,
        level: data.level || 3,
      }
    }
  })
}

// 上传文件-公共接口
export async function commonUpload({
  file,
  disableDetect,
  level,
  onProgress,
}: CommonUploadParams): Promise<ScanResult> {
  if (!file) return Promise.reject('文件不存在')
  const ext = file.name.split('.').pop() || ''
  const contentType = file.type

  return BaseApi({
    url: getBizApiUrl('/file/getTemporaryUrl'),
    method: 'get',
    data: { ext, contentType, level: level || 3 },
    error: false,
    success: false,
  } as enhanceOpt).then((data: GetTemporaryUrlResponse) => {
    const serverSideEncryption: Record<string, string> = {}
    if (level && [1, 2].includes(level)) {
      serverSideEncryption['x-obs-server-side-encryption'] = 'AES256'
    }
    return axios({
      method: 'PUT',
      url: data.object,
      withCredentials: false,
      maxRedirects: 0,
      responseType: 'text',
      headers: {
        'Content-Type': contentType,
        ...serverSideEncryption,
      },
      data: file,
      timeout: 10 * 60 * 1000,
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        )
        onProgress && onProgress(percentCompleted)
      },
      validateStatus: function (status) {
        return status >= 200
      },
    }).then((rel: AxiosResponse) => {
      const { status } = rel
      if (status === 200) {
        if (!disableDetect) {
          // 需要健康检测
          return BaseApi({
            url: getBizApiUrl('/file/scan'),
            method: 'post',
            data: { url: data.object, level: level || 3 },
            error: false,
            success: false,
          } as enhanceOpt).then(async (res: ScanResponse) => {
            if (res.success) {
              // status状态 0-初始态 1-已提交 2-检测中 3-检测通过 4-检测未通过 5-检测超时
              let result: ScanResult
              if (res.object?.status < 3 && !!res.object?.id) {
                result = await queryScanDetail({
                  id: res.object.id,
                  url: data.object,
                  file,
                  level: level || 3,
                })
              } else {
                result = {
                  url: data.object,
                  publicUrl: res.object?.publicUrl || data.object,
                  status: res.object?.status || 0,
                  name: file.name,
                  size: file.size,
                  type: file.type,
                  level: level || 3,
                  reason: res.object?.reason
                }
              }
              if (result.status === 3) {
                return result
              } else {
                return Promise.reject(
                  result.status === 4 ? result.reason : '文件检测超时'
                )
              }
            } else {
              return Promise.reject(res.message || '检测失败')
            }
          })
        } else {
          // 不需要健康检测
          if (data.status === 3) {
            return Promise.resolve({
              url: data.object,
              publicUrl: data.object,
              status: data.status,
              name: file.name,
              size: file.size,
              type: file.type,
              level: level || 3,
            })
          } else {
            return Promise.reject(
              data.status === 4 ? '文件检测不通过' : '文件检测超时'
            )
          }
        }
      } else {
        return Promise.reject('上传失败')
      }
    })
  }).catch((err) => {
    return Promise.reject(err)
  })
}